// Administração → Formulários (pedido 2026-10-05). Lista os formulários da
// empresa; criar abre direto o editor (admin.forms.$id.tsx).
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Check,
  ClipboardList,
  Copy,
  CopyPlus,
  ExternalLink,
  FilePlus2,
  Inbox,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import {
  blankForm,
  cadastroContratoTemplate,
  gpsDriveFormsService,
  publicFormUrl,
  type CompanyForm,
  type FormInput,
  FORMS_QUERY_KEY,
} from "@/services/gpsdrive-forms.service";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/forms/")({
  component: () => (
    <AdminAccessGate action="formularios">
      <FormsListPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Formulários — Administração — Wp Rastreamento" }] }),
});


function toInput(f: CompanyForm): FormInput {
  return {
    title: f.title,
    description: f.description,
    active: f.active,
    sections: f.sections,
    theme: f.theme,
    successTitle: f.successTitle,
    successMessage: f.successMessage,
  };
}

function FormsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: forms, isLoading, error } = useQuery({
    queryKey: FORMS_QUERY_KEY,
    queryFn: () => gpsDriveFormsService.list(),
    retry: false,
  });
  const [toDelete, setToDelete] = useState<CompanyForm | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (input: FormInput) => gpsDriveFormsService.create(input),
    onSuccess: (f) => {
      void qc.invalidateQueries({ queryKey: FORMS_QUERY_KEY });
      void navigate({ to: "/admin/forms/$id", params: { id: f.id } });
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Não foi possível criar"),
  });
  const duplicate = useMutation({
    mutationFn: (id: string) => gpsDriveFormsService.duplicate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FORMS_QUERY_KEY }),
    onError: (e) => setActionError(e instanceof Error ? e.message : "Não foi possível duplicar"),
  });
  const toggle = useMutation({
    mutationFn: (f: CompanyForm) => gpsDriveFormsService.update(f.id, { ...toInput(f), active: !f.active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FORMS_QUERY_KEY }),
    onError: (e) => setActionError(e instanceof Error ? e.message : "Não foi possível salvar"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => gpsDriveFormsService.remove(id),
    onSuccess: () => {
      setToDelete(null);
      void qc.invalidateQueries({ queryKey: FORMS_QUERY_KEY });
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Não foi possível excluir"),
  });

  const copyLink = async (f: CompanyForm) => {
    try {
      await navigator.clipboard.writeText(publicFormUrl(f.slug));
      setCopied(f.id);
      setTimeout(() => setCopied((c) => (c === f.id ? null : c)), 1800);
    } catch {
      // sem clipboard (http/permissão) — o link continua visível no card
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Formulários</h1>
          <p className="text-sm text-muted-foreground">
            Monte formulários com a cara da sua empresa e envie o link para o cliente preencher. As respostas
            aparecem aqui.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={create.isPending} onClick={() => create.mutate(cadastroContratoTemplate())}>
            <Sparkles className="mr-2 h-4 w-4" /> Modelo Cadastro + Contrato
          </Button>
          <Button disabled={create.isPending} onClick={() => create.mutate(blankForm())}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-2 h-4 w-4" />}
            Novo formulário
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center p-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      {forms && forms.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed p-12 text-center">
          <ClipboardList className="h-10 w-10 text-muted-foreground" />
          <p className="font-semibold">Nenhum formulário ainda</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Comece pelo modelo de Cadastro + Contrato (dados pessoais, endereço, telefones, plano e veículo) ou crie um
            em branco.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {forms?.map((f) => {
          const questions = f.sections.reduce((n, s) => n + s.fields.length, 0);
          return (
            <div
              key={f.id}
              className="group overflow-hidden rounded-2xl border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Link to="/admin/forms/$id" params={{ id: f.id }} className="block">
                <div
                  className="relative h-24 overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${f.theme.primaryColor}, color-mix(in srgb, ${f.theme.primaryColor} 45%, #a855f7))`,
                  }}
                >
                  {f.bannerUrl && (
                    <img
                      src={resolveGpsDriveUrl(f.bannerUrl)}
                      alt=""
                      className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  {f.unreadCount > 0 && (
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-900 shadow">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
                      {f.unreadCount} nova{f.unreadCount > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </Link>
              <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <Link to="/admin/forms/$id" params={{ id: f.id }} className="min-w-0">
                    <p className="truncate font-semibold hover:underline">{f.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.sections.length} etapa{f.sections.length > 1 ? "s" : ""} · {questions} pergunta
                      {questions !== 1 ? "s" : ""} · editado{" "}
                      {formatDistanceToNow(new Date(f.updatedAt), { addSuffix: true, locale: ptBR })}
                    </p>
                  </Link>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={cn("text-xs", f.active ? "text-emerald-600" : "text-muted-foreground")}>
                      {f.active ? "Ativo" : "Pausado"}
                    </span>
                    <Switch
                      checked={f.active}
                      disabled={toggle.isPending}
                      onCheckedChange={() => toggle.mutate(f)}
                      aria-label="Ativar formulário"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="gap-1">
                    <Inbox className="h-3 w-3" /> {f.responseCount} resposta{f.responseCount !== 1 ? "s" : ""}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" asChild>
                    <Link to="/admin/forms/$id" params={{ id: f.id }}>
                      <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void copyLink(f)}>
                    {copied === f.id ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                    {copied === f.id ? "Copiado" : "Link"}
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={publicFormUrl(f.slug)} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Abrir
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Duplicar"
                    disabled={duplicate.isPending}
                    onClick={() => duplicate.mutate(f.id)}
                  >
                    <CopyPlus className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Excluir" onClick={() => setToDelete(f)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir "{toDelete?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              O link para de funcionar e as {toDelete?.responseCount ?? 0} respostas recebidas são apagadas. Não dá para
              desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (toDelete) remove.mutate(toDelete.id);
              }}
            >
              {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
